import MapGeocodingApiService from "../../infrastructure/api/mapGeocoding";
import { mapConfig } from "../../services/map/mapConfig";
import {
  MapLocation,
  MapSearchOptions,
  MapSearchResult,
  MapServiceError,
  MapTileConfig,
  ReverseGeocodeOptions,
} from "../../services/map/types";
import type { IMapProvider } from "./IMapProvider";

interface ApiSearchResult {
  id?: string;
  title?: string;
  address?: string;
  lat?: number;
  lng?: number;
  city?: string;
  district?: string;
}

interface ApiLocationResult {
  lat?: number;
  lng?: number;
  address?: string;
  province?: string;
  city?: string;
  district?: string;
  formattedAddress?: string;
  provider?: string;
}

const ERROR_MESSAGES: Record<string, string> = {
  NETWORK_ERROR: "اتصال اینترنت برقرار نیست. لطفاً اتصال خود را بررسی کنید.",
  TIMEOUT: "زمان درخواست به پایان رسید. لطفاً دوباره تلاش کنید.",
  QUOTA_EXCEEDED: "سقف استفاده از سرویس نقشه به پایان رسیده است.",
  RATE_LIMIT: "تعداد درخواست‌ها بیش از حد مجاز است. لطفاً کمی صبر کنید.",
  INVALID_API_KEY: "کلید API نقشه نشان در تنظیمات سیستم ثبت نشده است.",
  NO_RESULTS: "نتیجه‌ای برای جستجوی شما یافت نشد.",
  INVALID_COORDINATES: "مختصات انتخاب‌شده معتبر نیست.",
  UNKNOWN: "خطای ناشناخته در سرویس نقشه رخ داد.",
};

function createTimeoutSignal(
  externalSignal?: AbortSignal,
  timeoutMs = mapConfig.apiTimeoutMs
): { signal: AbortSignal; cleanup: () => void } {
  const controller = new AbortController();
  const timeoutId = window.setTimeout(() => controller.abort(), timeoutMs);

  const onExternalAbort = () => controller.abort();
  externalSignal?.addEventListener("abort", onExternalAbort);

  return {
    signal: controller.signal,
    cleanup: () => {
      window.clearTimeout(timeoutId);
      externalSignal?.removeEventListener("abort", onExternalAbort);
    },
  };
}

function mapApiError(message?: string): MapServiceError {
  const normalized = (message || "").trim();

  if (normalized.includes("سقف")) {
    return new MapServiceError("QUOTA_EXCEEDED", ERROR_MESSAGES.QUOTA_EXCEEDED);
  }

  if (normalized.includes("بیش از حد")) {
    return new MapServiceError("RATE_LIMIT", ERROR_MESSAGES.RATE_LIMIT);
  }

  if (normalized.includes("کلید API")) {
    return new MapServiceError("INVALID_API_KEY", ERROR_MESSAGES.INVALID_API_KEY);
  }

  return new MapServiceError("UNKNOWN", normalized || ERROR_MESSAGES.UNKNOWN);
}

function toSearchResult(item: ApiSearchResult, index: number): MapSearchResult | null {
  if (typeof item.lat !== "number" || typeof item.lng !== "number") {
    return null;
  }

  return {
    id: item.id || `${item.lat}-${item.lng}-${index}`,
    title: item.title?.trim() || "مکان",
    address: item.address?.trim() || item.title?.trim() || "",
    lat: item.lat,
    lng: item.lng,
    city: item.city,
    district: item.district,
  };
}

function toMapLocation(data: ApiLocationResult): MapLocation {
  return {
    lat: Number(data.lat),
    lng: Number(data.lng),
    address: data.address || "",
    province: data.province,
    city: data.city,
    district: data.district,
    formattedAddress: data.formattedAddress,
    provider: "neshan",
  };
}

export class NeshanMapProvider implements IMapProvider {
  readonly name = "neshan" as const;

  getTileConfig(): MapTileConfig {
    return {
      provider: "neshan",
      mapType: mapConfig.defaultMapType,
      apiKey: "",
    };
  }

  async search(
    query: string,
    options: MapSearchOptions = {}
  ): Promise<MapSearchResult[]> {
    const term = query.trim();
    if (!term) {
      return [];
    }

    const [defaultLat, defaultLng] = mapConfig.defaultCenter;
    const lat = options.lat ?? defaultLat;
    const lng = options.lng ?? defaultLng;
    const limit = options.limit ?? 5;

    const { cleanup } = createTimeoutSignal(options.signal);
    try {
      const response = await MapGeocodingApiService.search({ term, lat, lng });

      if (!response?.isSuccess) {
        throw mapApiError(response?.message);
      }

      const items = Array.isArray(response.data) ? response.data : [];
      return items
        .map((item: ApiSearchResult, index: number) => toSearchResult(item, index))
        .filter((item: MapSearchResult | null): item is MapSearchResult => item !== null)
        .slice(0, limit);
    } catch (error) {
      if (error instanceof MapServiceError) {
        throw error;
      }

      if (error instanceof DOMException && error.name === "AbortError") {
        throw new MapServiceError("TIMEOUT", ERROR_MESSAGES.TIMEOUT);
      }

      if (!window.navigator.onLine) {
        throw new MapServiceError("NETWORK_ERROR", ERROR_MESSAGES.NETWORK_ERROR);
      }

      throw new MapServiceError("NETWORK_ERROR", ERROR_MESSAGES.NETWORK_ERROR);
    } finally {
      cleanup();
    }
  }

  async reverseGeocode(
    lat: number,
    lng: number,
    options: ReverseGeocodeOptions = {}
  ): Promise<MapLocation> {
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
      throw new MapServiceError(
        "INVALID_COORDINATES",
        ERROR_MESSAGES.INVALID_COORDINATES
      );
    }

    const { cleanup } = createTimeoutSignal(options.signal);
    try {
      const response = await MapGeocodingApiService.reverseGeocode({ lat, lng });

      if (!response?.isSuccess || !response?.data) {
        throw mapApiError(response?.message);
      }

      return toMapLocation(response.data as ApiLocationResult);
    } catch (error) {
      if (error instanceof MapServiceError) {
        throw error;
      }

      if (error instanceof DOMException && error.name === "AbortError") {
        throw new MapServiceError("TIMEOUT", ERROR_MESSAGES.TIMEOUT);
      }

      if (!window.navigator.onLine) {
        throw new MapServiceError("NETWORK_ERROR", ERROR_MESSAGES.NETWORK_ERROR);
      }

      throw new MapServiceError("NETWORK_ERROR", ERROR_MESSAGES.NETWORK_ERROR);
    } finally {
      cleanup();
    }
  }
}

export function getMapErrorMessage(error: unknown): string {
  if (error instanceof MapServiceError) {
    return error.message;
  }
  return ERROR_MESSAGES.UNKNOWN;
}
